import type { ClaudeAssessment, SaveClaudeAssessmentParams, EpicAssessmentSummary, EpicAssessmentSummaryQueryParams } from '../shared/types';
import { authenticatedFetch } from '../shared/authenticatedFetch';
import { ApiError } from '../shared/apiError';

export async function getClaudeAssessment(apiUrl: string, epicId: string): Promise<ClaudeAssessment | null> {
  let response: Response;
  try {
    response = await authenticatedFetch(`${apiUrl}/epics/${epicId}/assessments/claude`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }

  const data = await response.json();
  return data;
}

export async function saveClaudeAssessment(apiUrl: string, params: SaveClaudeAssessmentParams): Promise<void> {
  await authenticatedFetch(`${apiUrl}/epics/assessments/claude`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      epicId: params.epicId,
      capexPercentage: params.capexPercentage,
      rndPercentage: params.rndPercentage,
      capexRationale: params.capexRationale,
      rndRationale: params.rndRationale,
      capexEligibleWork: params.capexEligibleWork,
      capexNonEligibleWork: params.capexNonEligibleWork,
      rndEligibleWork: params.rndEligibleWork,
      rndNonEligibleWork: params.rndNonEligibleWork,
    }),
  });
}

export async function getBatchClaudeAssessments(apiUrl: string, epicIds: string[]): Promise<Map<string, ClaudeAssessment>> {
  if (epicIds.length === 0) {
    return new Map();
  }

  const epicIdsParam = epicIds.map(id => `epicIds=${id}`).join('&');
  const response = await authenticatedFetch(`${apiUrl}/epics/assessments/claude?${epicIdsParam}`);

  const data = await response.json();
  const assessmentsMap = new Map<string, ClaudeAssessment>();

  for (const assessment of data) {
    assessmentsMap.set(assessment.epic_id, assessment);
  }

  return assessmentsMap;
}

export async function hasAnyAssessedEpics(apiUrl: string, epicIds: string[], reviewId: string): Promise<boolean> {
  if (epicIds.length === 0) {
    return false;
  }

  const epicIdsParam = epicIds.map(id => `epicIds=${id}`).join('&');
  const response = await authenticatedFetch(
    `${apiUrl}/projects/has-assessed-epics?${epicIdsParam}&reviewId=${encodeURIComponent(reviewId)}`
  );

  const data = await response.json();
  return data.hasAssessed;
}

export async function getEpicAssessmentSummary(
  apiUrl: string,
  params: EpicAssessmentSummaryQueryParams,
): Promise<EpicAssessmentSummary[]> {
  const queryString = `projectName=${encodeURIComponent(params.projectName)}&fiscalYearQuarter=${encodeURIComponent(params.fiscalYearQuarter)}`;
  const fullUrl = `${apiUrl}/epic-assessments/summary?${queryString}`;

  const response = await authenticatedFetch(fullUrl);

  const data: EpicAssessmentSummary[] = await response.json();
  console.log('ProjectApproval_ Fetched epic assessment summary:', data);
  return data;
}
